<script setup>
import ListingForm from '@/components/Listing/ListingForm.vue'
import { useApi } from '@/composables/useApi'
import { useListingStore } from '@/stores/listing'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    requiresAuth: true,
    roles: ['surgeon_secretary'],
  },
})

const router = useRouter()
const listingStore = useListingStore()
const initialData = ref(null)
const loadError = ref('')
const isLoading = ref(true)

const fetchInitialData = async () => {
  isLoading.value = true
  loadError.value = ''

  try {
    const { data, error } = await useApi('/api/listings/prepare-store')
    if (error.value)
      throw error.value

    initialData.value = data.value?.data ?? {}
  }
  catch (err) {
    loadError.value = 'Unable to load the initial data.'
    console.error('Failed to load listing defaults', err)
  }
  finally {
    isLoading.value = false
  }
}

onMounted(fetchInitialData)

const handleSubmit = async payload => {
  try {
    const listing = await listingStore.createListing(payload)

    // Redirect alla dashboard o alla pagina di dettaglio
    router.push({
      name: 'surgeon_secretary-surgeon-secretary-dashboard-page',
      query: { created: listing.id },
    })
  }
  catch (err) {
    // Gli errori sono già gestiti nello store e mostrati nel form
    console.error('Failed to create listing', err)
  }
}





const handleCancel = () => {
  router.push({ name: 'surgeon_secretary-surgeon-secretary-dashboard-page' })
}
</script>

<template>
  <VContainer class="listing-create-shell">
    <VRow justify="center">
      <VCol
        cols="12"
        md="10"
        lg="11"
      >
        <VCard class="card-ghost">
          <div class="text-h4 pa-1 d-flex align-center justify-space-between">
            <VBtn
              variant="text"
              class="back-btn"
              @click="handleCancel"
            >
              Back to dashboard
            </VBtn>
          </div>

          <VCardText>
            <VAlert
              v-if="loadError"
              type="error"
              class="mb-6"
            >
              {{ loadError }}
            </VAlert>

            <VProgressLinear
              v-else-if="isLoading"
              indeterminate
              class="mb-6"
            />

            <ListingForm
              v-else
              :initial-data="initialData"
              @submit="handleSubmit"
              @cancel="handleCancel"
            />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style scoped>
.listing-create-shell {
  --listing-bg-base: rgba(var(--v-theme-surface), 0.42);
  --listing-card-bg: rgba(var(--v-theme-surface), 0.9);
  --listing-card-border: rgba(var(--v-theme-on-surface), 0.1);
  --listing-field-bg: rgba(var(--v-theme-surface-bright), 0.84);
  --listing-field-border: rgba(var(--v-theme-on-surface), 0.14);
  --listing-tonal-active: rgba(var(--v-theme-primary), 0.16);
  --listing-tonal-hover: rgba(var(--v-theme-primary), 0.1);
  background: linear-gradient(180deg, var(--listing-bg-base) 0%, rgba(var(--v-theme-surface), 0.18) 100%);
  border-radius: 16px;
  padding: 14px;
}

.listing-create-card {
  background: var(--listing-card-bg);
  border: 1px solid var(--listing-card-border);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
}

.back-btn {
  color: rgba(var(--v-theme-primary), 0.9);
}

.listing-create-shell :deep(.v-card) {
  background: rgba(var(--v-theme-surface), 0.88);
  border: 1px solid var(--listing-card-border);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

.listing-create-shell :deep(.v-field) {
  background: var(--listing-field-bg);
  border-color: var(--listing-field-border);
}

.listing-create-shell :deep(.v-field:hover) {
  border-color: rgba(var(--v-theme-primary), 0.26);
}

.listing-create-shell :deep(.v-field--focused) {
  box-shadow: 0 0 0 1px rgba(var(--v-theme-primary), 0.22) inset;
}

.listing-create-shell :deep(.v-btn--variant-flat),
.listing-create-shell :deep(.v-btn--variant-tonal) {
  box-shadow: none;
}

.listing-create-shell :deep(.v-btn--active),
.listing-create-shell :deep(.v-list-item--active),
.listing-create-shell :deep(.v-chip--selected) {
  background: var(--listing-tonal-active) !important;
  color: rgba(var(--v-theme-primary), 0.98) !important;
}

.listing-create-shell :deep(.v-list-item:hover),
.listing-create-shell :deep(.v-btn:hover) {
  background: var(--listing-tonal-hover);
}

.listing-create-shell :deep(.v-date-picker-controls__month-btn .v-btn__content),
.listing-create-shell :deep(.v-date-picker-controls__mode-btn .v-btn__content),
.listing-create-shell :deep(.v-date-picker-controls .v-btn) {
  color: rgba(var(--v-theme-on-surface), 0.86);
}

.listing-create-shell :deep(.v-date-picker-month__day--selected .v-btn) {
  background: var(--listing-tonal-active) !important;
  color: rgba(var(--v-theme-primary), 0.98) !important;
}

/* Keep "Create listing" action tied to draft/create state color (amber), independent from global primary. */
.listing-create-shell :deep(.action-btn--create-draft) {
  background-color: rgb(var(--v-theme-warning)) !important;
  color: rgb(var(--v-theme-on-warning)) !important;
}
</style>
